import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import ProjectService from "../../services/ProjectService";
import ProjectStoreService from "../../services/ProjectStoreService";
import CustomerService from "../../services/CustomerServices";
import CustomerStoreService from "../../services/CustomerStoreService";
import StockSalesReportService from "../../services/stockSalesReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function StockSalesReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";
  const loginData = StorageService.loginData();
  const employeeId = loginData?.employeeId || "";
  const roleId = loginData?.roleId || "";
  const [projectId, setProjectId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerStoreId, setCustomerStoreId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [projects, setProjects] = useState([]);
  const [stores, setStores] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerStores, setCustomerStores] = useState([]);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("");

  // ✅ PAGINATION STATES
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);


  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";

    const projectName = project.projectName || project.name || "";
    const categoryName = project.projectCategoryName || project.categoryName || project.category || "";
    const projectCode = project.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const loadDropdowns = async () => {
    try {
      let projectRes = [];
      let customerRes = [];

      try {
        projectRes = await ProjectService.getActiveProjectsByEmployee(
          companyId,
          employeeId
        );
      } catch (e) {
        console.error("Project API failed", e);
      }

      try {
        customerRes = await CustomerService.getByCompanyId(companyId);
      } catch (e) {
        console.error("Customer API failed", e);
      }

      setProjects(projectRes?.data || projectRes || []);
      
      const activeCustomers = (customerRes?.data || customerRes || []).filter(
        (c) => c.status === "Active"
      );
      setCustomers(activeCustomers);
    } catch (error) {
      console.error("Unexpected error in dropdowns:", error);
    }
  };

  const loadStoresByProject = async (projId) => {
    if (!projId) {
      setStores([]);
      return;
    }
    try {
      const res = await ProjectStoreService.getProjectStoreById(projId);
      setStores(res?.data || res || []);
    } catch (err) {
      console.error("Store load error", err);
      setStores([]);
    }
  };

  const loadCustomerStoresByCustomer = async (custId) => {
    if (!custId) {
      setCustomerStores([]);
      return;
    }
    try {
      const res = await CustomerStoreService.getByCustomer(custId);
      const list = res?.data || res || [];
      setCustomerStores(list.filter((cs) => cs.status === "Active"));
    } catch (err) {
      console.error("Customer Store load error", err);
      setCustomerStores([]);
    }
  };

  const loadReport = async (pageNumber = page, overrides = {}) => {
    const requestStartDate = overrides.startDate ?? startDate;
    const requestEndDate = overrides.endDate ?? endDate;

    if (requestStartDate && !requestEndDate) {
      showMessage("From Date and To Date are required");
      return;
    }

    if (requestStartDate && requestEndDate && new Date(requestStartDate) > new Date(requestEndDate)) {
      showMessage("From Date should be less than or equal to To Date");
      return;
    }

    setLoading(true);
    try {
      setReports([]);
      const res = await StockSalesReportService.getReport({
        companyId: companyId || undefined,
        employeeId,
        roleId,
        projectId: projectId || undefined,
        storeId: storeId || undefined,
        customerId: customerId || undefined,
        customerStoreId: customerStoreId || undefined,
        startDate: requestStartDate || undefined,
        endDate: requestEndDate || undefined,
        page: pageNumber,
        limit: limit,
      });

      setReports(res?.data || []);
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching stock sales report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const today = new Date();
      setEndDate(today.toISOString().split("T")[0]);
      loadReport(1);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setProjectId("");
    setStoreId("");
    setCustomerId("");
    setCustomerStoreId("");
    setStartDate("");
    const today = new Date();
    setEndDate(today.toISOString().split("T")[0]);
    setStores([]);
    setCustomerStores([]);
    setReports([]);
    setPage(1);
    setTotalPages(0);
    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      do {
        const res = await StockSalesReportService.getReport({
          companyId: companyId || undefined,
          employeeId,
          roleId,
          projectId: projectId || undefined,
          storeId: storeId || undefined,
          customerId: customerId || undefined,
          customerStoreId: customerStoreId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          page: currentPage,
          limit: 100, // fetch larger batches for export speed
        });

        const data = res?.data || [];
        const pagination = res?.pagination || {};

        allData = [...allData, ...data];
        totalPages = pagination.totalPages || 1;
        currentPage++;
      } while (currentPage <= totalPages);

      return allData;
    } catch (error) {
      console.error("PDF/Excel fetch error:", error);
      return [];
    }
  };

  const exportExcel = async () => {
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const parseReportDate = (value) => {
      if (!value) return Number.MAX_SAFE_INTEGER;
      if (typeof value === "number") {
        return value > 25569 ? (value - 25569) * 86400 * 1000 : value;
      }
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    };

    const toExcelDateSerial = (value) => {
      if (!value) return "-";
      if (typeof value === "number" && value > 25569) {
        return value;
      }
      const parsed = parseReportDate(value);
      if (!Number.isFinite(parsed) || parsed === Number.MAX_SAFE_INTEGER) return "-";
      return Math.floor(parsed / (24 * 60 * 60 * 1000)) + 25569;
    };

    const startTime = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
    const endTime = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;

    const filteredData = allData.filter((item) => {
      const saleTime = parseReportDate(item.saleDate);
      if (startTime && saleTime < startTime) return false;
      if (endTime && saleTime > endTime) return false;
      return true;
    });

    const sortedData = filteredData.sort((a, b) => {
      const dateA = parseReportDate(a.saleDate);
      const dateB = parseReportDate(b.saleDate);
      return dateA - dateB;
    });

    if (!sortedData.length) {
      showMessage("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];

    // Get filter labels
    let projectLabel = "All";
    if (projectId) {
      const found = projects.find((p) => String(p.id) === String(projectId));
      projectLabel = formatProjectDisplay(found || { projectName: projectId });
    }

    let storeLabel = "All";
    if (storeId) {
      const found = stores.find((s) => String(s.id) === String(storeId));
      storeLabel = found?.storeName || storeId;
    }

    let customerLabel = "All";
    if (customerId) {
      const found = customers.find((c) => String(c.id) === String(customerId));
      customerLabel = found?.customerName || customerId;
    }

    let customerStoreLabel = "All";
    if (customerStoreId) {
      const found = customerStores.find((cs) => String(cs.id) === String(customerStoreId));
      customerStoreLabel = found?.customerStoreName || customerStoreId;
    }

    let row = 0;
    const companyName = activeCompany?.companyName || "Company";

    // STYLES
    const thin = { style: "thin" };
    const center = {
      horizontal: "center",
      vertical: "center",
      wrapText: true,
    };

    // COMPANY NAME (Top Left)
    XLSX.utils.sheet_add_aoa(ws, [[companyName]], { origin: { r: row, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, color: { rgb: "000000" }, sz: 12 },
    };

    // REPORT DATE (Top Right)
    XLSX.utils.sheet_add_aoa(
      ws,
      [[`Report Date: ${utility.formatExportDateTime(new Date())}`]],
      { origin: { r: row, c: 7 } }
    );
    ws[XLSX.utils.encode_cell({ r: row, c: 7 })].s = {
      font: { bold: true, color: { rgb: "000000" }, sz: 12 },
      alignment: { horizontal: "right", vertical: "center" },
    };

    row++;

    // TITLE
    XLSX.utils.sheet_add_aoa(ws, [["Stock Sales Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 8 } });
    const titleCell = XLSX.utils.encode_cell({ r: row, c: 0 });
    ws[titleCell].s = {
      font: { bold: true, sz: 16 },
      alignment: { horizontal: "center" },
    };

    row += 2;

    // FILTER INFO
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Project:", projectLabel, "", "Store:", storeLabel],
        ["Customer:", customerLabel, "", "Customer Store:", customerStoreLabel],
        [
          "From Date:",
          startDate ? utility.formatDateDDMMMYYYY(startDate) : "-",
          "",
          "To Date:",
          endDate ? utility.formatDateDDMMMYYYY(endDate) : "-",
        ],
      ],
      { origin: { r: row, c: 0 } }
    );

    const filterLabelStyle = {
      font: { bold: true, sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };

    const filterValueStyle = {
      font: { sz: 10 },
      alignment: { horizontal: "left", vertical: "center" },
      border: { top: thin, bottom: thin, left: thin, right: thin },
    };

    const boldLabelColumns = [0, 3];
    for (let i = 0; i < 3; i++) {
      for (let c = 0; c < 5; c++) {
        const cell = XLSX.utils.encode_cell({ r: row + i, c });
        if (!ws[cell]) continue;
        if (boldLabelColumns.includes(c)) {
          ws[cell].s = filterLabelStyle;
        } else {
          ws[cell].s = filterValueStyle;
        }
      }
    }
    row += 4;

    // TABLE HEADER
    const headers = [
      "Invoice No",
      "Sale Date",
      "Customer",
      "Customer Store",
      "Project",
      "Store Name",
      "Item Category",
      "Item Code",
      "Item Description",
      "UOM",
      "Quantity",
    ];

    const headerRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });

    headers.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      ws[cell].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border: {
          top: thin,
          bottom: thin,
          left: thin,
          right: thin,
        },
      };
    });
    row++;

    // TABLE DATA
    sortedData.forEach((reportRow) => {
      const saleDtSerial = utility.convertDateToExcelSerial(reportRow.saleDate);
      const rowData = [
        reportRow.invoiceNo || "-",
        saleDtSerial || "-",
        reportRow.customerName || "-",
        reportRow.customerStoreName || "-",
        formatProjectDisplay(reportRow),
        reportRow.storeName || "-",
        reportRow.itemCategoryName || "-",
        reportRow.itemCode || "-",
        reportRow.itemDescription || "-",
        reportRow.UOM || "-",
        toNumber(reportRow.quantity),
      ];

      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });

      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        const isQuantity = colIndex === 10;
        const isDate = colIndex === 1;
        ws[cell].s = {
          alignment: { horizontal: isQuantity ? "right" : (isDate ? "center" : "left"), vertical: "center" },
          border: {
            top: thin,
            bottom: thin,
            left: thin,
            right: thin,
          },
        };

        if (colIndex === 1 && saleDtSerial) {
          ws[cell].t = "n";
          ws[cell].v = saleDtSerial;
          ws[cell].z = "dd-mm-yyyy";
        }

        if (colIndex === 10) {
          ws[cell].t = "n";
          ws[cell].z = "0.000";
        }
      });
      row++;
    });

    // TOTALS ROW
    const totalQuantity = sortedData.reduce((sum, item) => sum + toNumber(item.quantity), 0);
    const totalsRow = ["", "", "", "", "", "", "", "", "", "Total:", totalQuantity];
    XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: { r: row, c: 0 } });
    totalsRow.forEach((_, colIndex) => {
      const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
      if (!ws[cell]) return;
      ws[cell].s = {
        font: { bold: true, sz: 10 },
        alignment: colIndex === 9 
          ? { horizontal: "right", vertical: "center" } 
          : { horizontal: "center", vertical: "center" },
        border: { top: thin, bottom: thin, left: thin, right: thin },
      };
      if (colIndex === 10) {
        ws[cell].t = "n";
        ws[cell].z = "#,##0.000";
      }
    });
    row++;

    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: row - 1, c: headers.length - 1 }
      })
    };

    // Set column widths
    ws["!cols"] = [
      { wch: 18 },
      { wch: 15 },
      { wch: 22 },
      { wch: 22 },
      { wch: 20 },
      { wch: 18 },
      { wch: 30 },
      { wch: 40 },
      { wch: 28 },
      { wch: 10 },
      { wch: 10 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "StockSalesReport");
    XLSX.writeFile(wb, "StockSalesReport.xlsx");
  };

  const exportPdf = async () => {
    const allData = await getAllReportData();

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    let projectLabel = "All";
    if (projectId) {
      const found = projects.find((p) => String(p.id) === String(projectId));
      projectLabel = formatProjectDisplay(found || { projectName: projectId });
    }

    let storeLabel = "All";
    if (storeId) {
      const found = stores.find((s) => String(s.id) === String(storeId));
      storeLabel = found?.storeName || storeId;
    }

    let customerLabel = "All";
    if (customerId) {
      const found = customers.find((c) => String(c.id) === String(customerId));
      customerLabel = found?.customerName || customerId;
    }

    let customerStoreLabel = "All";
    if (customerStoreId) {
      const found = customerStores.find((cs) => String(cs.id) === String(customerStoreId));
      customerStoreLabel = found?.customerStoreName || customerStoreId;
    }

    const doc = new jsPDF("landscape");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 14;
    const right = pageWidth - pageMargin;
    const left = pageMargin;
    const companyName = activeCompany?.companyName || "Company";
    const title = "Stock Sales Report";

    const drawHeader = (pageNumber) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      doc.setFont(undefined, "bold");
      doc.setFontSize(12);
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = 8;

      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      doc.text(companyName, left + 6, 23);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 35, { align: "center" });

      const titleWidth = doc.getTextWidth(title);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
    };

    drawHeader(1);
    let yPosition = 45;

    // Title already rendered above with centered layout

    // Filter Info
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");

    let yHeader = yPosition;
    let xLeft = 14;
    let xRight = 115;

    doc.text("Project:", xLeft, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(projectLabel), xLeft + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("Store:", xLeft, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(String(storeLabel), xLeft + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("Customer:", xLeft, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(String(customerLabel), xLeft + 35, yHeader + 10);

    doc.setFont(undefined, "bold");
    doc.text("Customer Store:", xRight, yHeader);
    doc.setFont(undefined, "normal");
    doc.text(String(customerStoreLabel), xRight + 35, yHeader);

    doc.setFont(undefined, "bold");
    doc.text("From Date:", xRight, yHeader + 5);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", xRight + 35, yHeader + 5);

    doc.setFont(undefined, "bold");
    doc.text("To Date:", xRight, yHeader + 10);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", xRight + 35, yHeader + 10);

    yPosition += 20;

    const sortedData = [...allData].sort((a, b) => {
      const dateA = new Date(a.saleDate || 0).getTime();
      const dateB = new Date(b.saleDate || 0).getTime();
      return dateA - dateB;
    });

    const body = sortedData.map((row, index) => [
      index + 1,
      row.invoiceNo || "-",
      utility.formatDateDDMMMYYYY(row.saleDate),
      row.customerName || "-",
      row.customerStoreName || "-",
      formatProjectDisplay(row),
      row.storeName || "-",
      row.itemCategoryName || "-",
      row.itemCode || "-",
      row.itemDescription || "-",
      row.UOM || "-",
      row.quantity ?? "-",
    ]);

    const totalQuantity = sortedData.reduce((sum, row) => {
      const quantity = Number(row.quantity);
      return Number.isFinite(quantity) ? sum + quantity : sum;
    }, 0);

    autoTable(doc, {
      startY: yPosition,
      margin: {
        top: 40,
        left: 14,
        right: 14,
      },
      head: [
        [
          "#",
          "Invoice No",
          "Sale Date",
          "Customer",
          "Customer Store",
          "Project",
          "Store Name",
          "Item Category",
          "Item Code",
          "Item Description",
          "UOM",
          "Quantity",
        ],
      ],
      body,
      foot: [
        [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "Total",
          totalQuantity.toFixed(3),
        ],
      ],
      showFoot: "lastPage",
      theme: "grid",
      styles: {
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: [0, 0, 0],
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        0: { halign: "right" },
        1: { halign: "left" },
        2: { halign: "center" },
        3: { halign: "left" },
        4: { halign: "left" },
        5: { halign: "left" },
        6: { halign: "left" },
        7: { halign: "left" },
        8: { halign: "left" },
        9: { halign: "left" },
        10: { halign: "left" },
        11: { halign: "right" }
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      didDrawPage: function (data) {
        if (data.pageNumber === 1) return;
        drawHeader(data.pageNumber);
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, {
        align: "center",
      });

      if (i === pageCount) {
        doc.setFontSize(9);
        doc.text("Prepared By", 20, pageHeight - 22);
        doc.text("Checked By", pageWidth / 2, pageHeight - 22, { align: "center" });
        doc.text("Approved By", pageWidth - 20, pageHeight - 22, { align: "right" });
      }
    }

    doc.save("StockSalesReport.pdf");
  };

  return (
    <>
      <Breadcrumb pageTitle="Stock Sales Report" secondTitle="Reports" />

      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />

      <div className="card p-4 bg-white shadow-sm">
        <div className="row g-3 mb-3">
          <div className="col-md-3">
            <label className="form-label">Project Name</label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => {
                const selectedProjectId = e.target.value;
                setProjectId(selectedProjectId);
                setStoreId("");
                loadStoresByProject(selectedProjectId);

                if (!selectedProjectId) {
                  setStartDate("");
                  return;
                }

                const selectedProject = projects.find(
                  (p) => String(p.id || p.projectId) === String(selectedProjectId)
                );

                if (selectedProject?.projectStartDate) {
                  const formattedDate = new Date(selectedProject.projectStartDate)
                    .toISOString()
                    .split("T")[0];
                  setStartDate(formattedDate);
                } else {
                  setStartDate("");
                }
              }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id || project.projectId} value={project.id || project.projectId}>
                  {formatProjectDisplay(project)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Store</label>
            <select
              className="form-select"
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              disabled={!projectId}
            >
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.storeId || store.id} value={store.storeId || store.id}>
                  {store.storeName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Customer</label>
            <select
              className="form-select"
              value={customerId}
              onChange={(e) => {
                const selectedCustomerId = e.target.value;
                setCustomerId(selectedCustomerId);
                setCustomerStoreId("");
                loadCustomerStoresByCustomer(selectedCustomerId);
              }}
            >
              <option value="">All Customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.customerName} ({c.customerCode})
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">Customer Store</label>
            <select
              className="form-select"
              value={customerStoreId}
              onChange={(e) => setCustomerStoreId(e.target.value)}
              disabled={!customerId}
            >
              <option value="">All Customer Stores</option>
              {customerStores.map((cs) => (
                <option key={cs.id} value={cs.id}>
                  {cs.customerStoreName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-3">
            <label className="form-label">From Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">To Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>

            <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        
        <hr />

        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>

            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Invoice No</th>
                <th>Sale Date</th>
                <th>Customer</th>
                <th>Customer Store</th>
                <th>Project</th>
                <th>Store Name</th>
                <th>Item Category</th>
                <th>Item Code</th>
                <th>Item Description</th>
                <th>UOM</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="text-center">
                    <div className="spinner-border text-primary" />
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center">
                    No data found
                  </td>
                </tr>
              ) : (
                reports.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{row.invoiceNo || "-"}</td>
                    <td>{utility.formatDateDDMMMYYYY(row.saleDate)}</td>
                    <td>{row.customerName || "-"}</td>
                    <td>{row.customerStoreName || "-"}</td>
                    <td>{formatProjectDisplay(row)}</td>
                    <td>{row.storeName || "-"}</td>
                    <td>{row.itemCategoryName || "-"}</td>
                    <td>{row.itemCode || "-"}</td>
                    <td>{row.itemDescription || "-"}</td>
                    <td>{row.UOM || "-"}</td>
                    <td>{row.quantity ?? "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                Total Records: <strong>{totalRecords}</strong>
              </div>

              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === 1}
                  onClick={() => loadReport(page - 1)}
                >
                  Prev
                </button>

                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button
                      key={p}
                      className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => loadReport(p)}
                    >
                      {p}
                    </button>
                  ))}

                <button
                  className="btn btn-sm btn-secondary"
                  disabled={page === totalPages}
                  onClick={() => loadReport(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
